// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import {
  SpecialTag,
  getSpecialTagLabel,
  hideStrikethroughPrice,
} from '@iheartjane/menu';
import {
  Flex,
  Hide,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { formatCurrency, titleize } from '@iheartjane/utils';
import last from 'lodash-es/last';

// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, Product, StoreSpecial } from '@jane/shared/models';
import {
  availableWeightsForMenuProduct,
  generateCustomLabel,
  getDiscountedUnitPrice,
  getUnitPrice,
} from '@jane/shared/util';

import { Brand, Category, Kind, Name } from '../legacyProductCard.utils';
import { useProductCardContext } from '../productCardProvider';
import {
  ProductPotency,
  getDosageAmountPackInfo,
  getLabResults,
  getPackTotal,
} from '../productPotency/productPotency';
import { FiveStarRating } from '../starRatings';
import { PRICE_LOOKUP } from './utils/productCardHelper';
import { useProductReviewsEnabled } from './utils/useProductReviewsEnabled';

const REVIEWS_REQUIRED_BEFORE_DISPLAY = 5;

const TruncateWrapper = styled.div<{ truncateOffset: number | false }>(
  ({ truncateOffset }) => [
    {
      maxWidth: truncateOffset ? `calc(100% - ${truncateOffset}px)` : '100%',
    },
  ]
);

const ReviewStars = ({ product }: { product: MenuProduct | Product }) => {
  return product?.aggregate_rating &&
    product?.review_count &&
    product?.review_count >= REVIEWS_REQUIRED_BEFORE_DISPLAY ? (
    <Flex alignItems="center" data-testid="product-rating">
      <FiveStarRating rating={product.aggregate_rating} />
      <Typography color="text-light">{product.review_count}</Typography>
    </Flex>
  ) : null;
};

export const MenuBasicProductInfo = ({
  fullDetails = true,
  applyTruncateOffset = false,
  selectedWeight,
  specialApplies,
}: {
  applyTruncateOffset?: boolean;
  fullDetails?: boolean;
  selectedWeight?: PriceId;
  specialApplies?: boolean;
}) => {
  const {
    appMode,
    availableWeights: specialWeights,
    defaultWeight,
    menuProduct: product,
    showOnlyWeights,
    store,
    userLocation,
    weightFilter,
  } = useProductCardContext();
  const mobile = useMobileMediaQuery({ size: 'legacy' });
  const specialTagLabel =
    store && product && getSpecialTagLabel({ defaultWeight, product, store });

  const productReviewsEnabled = useProductReviewsEnabled(
    appMode,
    userLocation,
    store
  );
  const hasReviews = Boolean(
    productReviewsEnabled &&
    product?.aggregate_rating &&
    product?.review_count &&
    product?.review_count >= REVIEWS_REQUIRED_BEFORE_DISPLAY
  );

  const hasLongName = product?.name ? product?.name.length >= 15 : false;

  const productKind = generateCustomLabel({
    appMode,
    store,
    attribute: product?.brand_subtype || product?.kind,
    fallback: product?.brand_subtype || titleize(product?.kind),
  });

  const availableWeights = (
    weightFilter && specialWeights
      ? specialWeights
      : availableWeightsForMenuProduct(product as MenuProduct, showOnlyWeights)
  ).map((price) => PRICE_LOOKUP[price.toUpperCase()]);

  const availablePrices =
    availableWeights.length === 0 || product?.amount
      ? 'EACH'
      : formatWeights(availableWeights);

  const brandText = (
    <Brand title={product?.brand || ''}>
      {product && product.brand ? product.brand : ''}
    </Brand>
  );

  const productLineage = generateCustomLabel({
    appMode,
    store,
    attribute: product?.category,
    isLineageLabel: true,
    fallback: titleize(product?.category),
  });

  // NOTE(elliot): Display pre roll pack info if product has lab results. (potencyInfo pioritizes lab results over other info)
  const displayPackInfo = Boolean(
    product &&
    getLabResults(product) &&
    getDosageAmountPackInfo(product) &&
    getPackTotal(product)
  );

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        {productLineage.length > 0 && (
          <Category
            style={{ minHeight: mobile ? '16px' : '24px' }}
            title={product?.category || ''}
            category={product?.category}
          >
            {productLineage}
          </Category>
        )}
        {specialApplies && specialTagLabel && (
          <Flex ml={8}>
            <SpecialTag label={specialTagLabel} />
          </Flex>
        )}
      </Flex>

      <TruncateWrapper truncateOffset={applyTruncateOffset && 40}>
        <Name title={product?.name}>{product?.name}</Name>
      </TruncateWrapper>

      <Typography color="text-light" as="div">
        {product && product.brand ? (
          <TruncateWrapper truncateOffset={applyTruncateOffset && 15}>
            {brandText}
          </TruncateWrapper>
        ) : (
          brandText
        )}
      </Typography>

      {fullDetails && product && (
        <>
          {productReviewsEnabled && <ReviewStars product={product} />}
          <Flex alignItems="center">
            <Kind title={productKind}>{productKind}</Kind>
            <Typography color="text-light" ml={4} truncateAt="auto">
              (
              {displayPackInfo
                ? getDosageAmountPackInfo(product)
                : availablePrices.toUpperCase()}
              )
            </Typography>
          </Flex>
        </>
      )}
      <ProductPotency
        product={product as MenuProduct}
        showLess={hasReviews && hasLongName}
        selectedWeight={selectedWeight}
      />
    </>
  );
};

const formatWeights = (availableWeights: string[]) => {
  switch (availableWeights.length) {
    case 1:
      return availableWeights[0];
    case 2:
      return `${availableWeights[0]}, ${availableWeights[1]}`;
    default:
      return `${availableWeights[0]} - ${last(availableWeights)}`;
  }
};

interface Props {
  currentSpecial?: StoreSpecial;
}

export const MenuProductPrices = ({ currentSpecial }: Props) => {
  const {
    defaultWeight,
    menuProduct: product,
    store,
  } = useProductCardContext();

  const originalPrice = getUnitPrice(product as MenuProduct, defaultWeight);

  const isProductSpecial = currentSpecial?.special_type === 'product';
  const discountedUnitPrice = getDiscountedUnitPrice(
    product as MenuProduct,
    defaultWeight
  );
  const unitPrice =
    isProductSpecial && discountedUnitPrice
      ? discountedUnitPrice
      : originalPrice;

  const defaultPriceId =
    product && product.amount ? '' : PRICE_LOOKUP[defaultWeight?.toUpperCase()];

  const isSale =
    isProductSpecial && originalPrice && originalPrice !== unitPrice;

  return (
    <Flex mt="auto">
      {isSale && !hideStrikethroughPrice(store?.state || '') && (
        <Typography color="primary" strikeThrough>
          <Hide as="span" isHidden>
            Original price{' '}
          </Hide>
          {formatCurrency(originalPrice)}
        </Typography>
      )}
      {unitPrice !== undefined && (
        <Typography color="primary" ml="auto" variant="body-semibold">
          {isSale && (
            <Hide as="span" isHidden>
              Sale price{' '}
            </Hide>
          )}
          {formatCurrency(unitPrice)}
          {defaultPriceId && (
            <Typography as="span" variant="body-semibold" color="primary">
              /{defaultPriceId}
            </Typography>
          )}
        </Typography>
      )}
    </Flex>
  );
};
